import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import ErrorRetry from "@/components/ErrorRetry";
import { CardSkeleton } from "@/components/Skeleton";
import { ArrowRight, ArrowUpRight, ArrowDownRight, Minus } from "lucide-react";

/** Owner Snapshot — the money moment.
 *
 *  Numbers first, labels small, one accent for what needs attention. No gauges, no red
 *  walls: a clinic owner reading this is deciding what to do next, and a wall of alarm
 *  colours tells them everything is equally urgent, which is the same as telling them
 *  nothing.
 *
 *  Every figure links to the action that improves it. A number with no next action does
 *  not belong on this screen.
 */

function money(value) {
  return new Intl.NumberFormat(undefined, {
    style: "currency", currency: "USD", maximumFractionDigits: 0,
  }).format(value ?? 0);
}

function Figure({ value, label, sub, tone = "plain", onClick, testid }) {
  const tones = {
    plain: "text-foreground",
    attention: "text-amber-700",
  };
  return (
    <button onClick={onClick} data-testid={testid}
            className="text-left w-full bg-card border border-border rounded-lg p-5 hover:shadow-sm hover:-translate-y-[1px] transition-all duration-200">
      <p className={`font-heading text-3xl font-bold tabular-nums tracking-tight ${tones[tone]}`}>
        {value}
      </p>
      <p className="text-sm font-medium mt-1">{label}</p>
      {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
      <span className="inline-flex items-center gap-1 text-xs text-muted-foreground mt-3">
        <ArrowRight className="w-3 h-3" /> {onClick ? "Take action" : ""}
      </span>
    </button>
  );
}

function Trend({ points }) {
  if (points === null || points === undefined) {
    return (
      <span className="text-xs text-muted-foreground" data-testid="adherence-trend">
        Not enough history yet to compare
      </span>
    );
  }
  const flat = points === 0;
  const up = points > 0;
  const Icon = flat ? Minus : up ? ArrowUpRight : ArrowDownRight;
  const tone = flat ? "text-muted-foreground" : up ? "text-emerald-700" : "text-amber-700";
  return (
    <span className={`text-xs inline-flex items-center gap-1 ${tone}`} data-testid="adherence-trend">
      <Icon className="w-3.5 h-3.5" />
      {flat ? "Level with last month"
            : `${up ? "+" : ""}${points} points vs last month`}
    </span>
  );
}

function Section({ title, note, children, testid }) {
  return (
    <section data-testid={testid} className="space-y-3">
      <div>
        <h2 className="font-heading text-lg font-semibold">{title}</h2>
        {note && <p className="text-sm text-muted-foreground mt-0.5">{note}</p>}
      </div>
      {children}
    </section>
  );
}

export default function Snapshot() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(() => {
    setData(null);
    setError(null);
    api.get("/owner/snapshot")
      .then((r) => setData(r.data))
      .catch((e) => { setData(false); setError(e); });
  }, []);

  useEffect(() => { load(); }, [load]);

  if (data === null) return <div className="space-y-4"><CardSkeleton lines={4} /><CardSkeleton lines={4} /></div>;

  if (data === false) {
    return (
      <div className="space-y-4" data-testid="snapshot-page">
        <h1 className="font-heading text-3xl font-bold tracking-tight">Snapshot</h1>
        <ErrorRetry
          error={error}
          onRetry={load}
          title="These figures couldn't be loaded"
          note="Nothing here is current. Don't read a blank snapshot as a flat month."
          testid="snapshot-error"
        />
      </div>
    );
  }

  const { membership, adherence, visits } = data;

  return (
    <div className="space-y-8" data-testid="snapshot-page">
      <div>
        <h1 className="font-heading text-3xl font-bold tracking-tight">Snapshot</h1>
        <p className="text-sm text-muted-foreground mt-1">
          How the practice is doing, and what to do about it.
        </p>
      </div>

      <Section title="Membership" note={membership.payment_note} testid="snapshot-membership">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Figure testid="figure-members" value={membership.active_members}
                  label="Active members" sub="Paying, not paused"
                  onClick={() => navigate(membership.link)} />
          <Figure testid="figure-mrr" value={money(membership.monthly_recurring_value)}
                  label="Monthly recurring value"
                  sub="Normalised across monthly, quarterly and annual plans"
                  onClick={() => navigate(membership.link)} />
          <Figure testid="figure-renewals" value={membership.renewals_due_30d}
                  label="Renewals due in 30 days"
                  tone={membership.renewals_due_30d > 0 ? "attention" : "plain"}
                  sub="Reach out before they lapse"
                  onClick={() => navigate(membership.link)} />
        </div>

        {membership.renewals?.length > 0 && (
          <div className="bg-card border border-border rounded-lg divide-y divide-border"
               data-testid="renewals-list">
            {membership.renewals.map((r) => (
              <button key={r.patient_id} data-testid={`renewal-${r.patient_id}`}
                      onClick={() => navigate(`/patients/${r.patient_id}`)}
                      className="w-full text-left px-5 py-3 flex items-center justify-between gap-3 hover:bg-secondary/50">
                <span className="text-sm font-medium">{r.patient_name}
                  <span className="text-muted-foreground font-normal"> · {r.plan}</span>
                </span>
                <span className="text-xs text-muted-foreground shrink-0">
                  {r.days_until === 0 ? "Renews today" : `in ${r.days_until} day${r.days_until === 1 ? "" : "s"}`}
                </span>
              </button>
            ))}
          </div>
        )}
      </Section>

      <Section title="Protocol adherence"
               note="Members current on the cadence their programme asks for."
               testid="snapshot-adherence">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Figure testid="figure-adherence"
                  value={adherence.percent_current === null ? "—" : `${adherence.percent_current}%`}
                  label="Current on cadence"
                  sub={`${adherence.current} of ${adherence.members_measured} members`}
                  onClick={() => navigate(adherence.link)} />
          <Figure testid="figure-overdue" value={adherence.overdue}
                  label="Overdue" tone={adherence.overdue > 0 ? "attention" : "plain"}
                  sub="Labs or check-ins past due"
                  onClick={() => navigate(adherence.link)} />
          <div className="bg-card border border-border rounded-lg p-5 flex flex-col justify-center">
            <p className="text-sm font-medium">Compared with last month</p>
            <div className="mt-2"><Trend points={adherence.trend_points} /></div>
            <p className="text-xs text-muted-foreground mt-3">
              Worked out from when labs were actually reviewed and check-ins actually
              happened — not a stored figure.
            </p>
          </div>
        </div>
      </Section>

      {visits && (
        <Section title="Visits"
                 note="Shown because this clinic runs on appointments."
                 testid="snapshot-visits">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Figure testid="figure-noshows" value={visits.no_shows_30d}
                    label="No-shows this month"
                    tone={visits.no_shows_30d > 0 ? "attention" : "plain"}
                    sub="Time the clinic could not sell twice"
                    onClick={() => navigate(visits.link)} />
            <Figure testid="figure-lost" value={money(visits.estimated_value_lost)}
                    label="Estimated value missed" sub="Based on those members' plans"
                    onClick={() => navigate(visits.link)} />
            <Figure testid="figure-upcoming" value={visits.upcoming_booked}
                    label="Booked ahead" sub="Appointments still to come"
                    onClick={() => navigate(visits.link)} />
          </div>
        </Section>
      )}
    </div>
  );
}
